import type { ComponentPropsWithoutRef, JSX } from 'react';
import { styled, css } from 'styled-components';
import type { ResponsiveObject } from '../../private/hooks/useResponsiveProp/useResponsiveProp';
import { useResponsiveProp } from '../../private/hooks/useResponsiveProp';
import type { Spacings } from '../../css/designTokens/spacing';

type DividerComponentProps = {
  $orientation: 'horizontal' | 'vertical';
  $inset: Spacings;
};

export type DividerProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * Orientation of the divider.
   */
  orientation?: ResponsiveObject<'horizontal' | 'vertical'> | 'horizontal' | 'vertical';
  /**
   * The amount of space to inset the divider.
   */
  inset?: ResponsiveObject<Spacings> | Spacings;
};

const horizontalBorderCss = css`
  border-top-color: var(--wui-color-border);
  border-top-style: solid;
  border-top-width: 1px;
  clear: both; /* for horizontal dividers, ensure it clears any floats */
  height: 0;
  margin-left: var(--wui-divider-inset);
  margin-right: var(--wui-divider-inset);
`;

const verticalBorderCss = css`
  background-color: var(--wui-color-border);
  max-width: 1px;
  min-height: 100%;
  width: 1px;
  margin-top: var(--wui-divider-inset);
  margin-bottom: var(--wui-divider-inset);
`;

const DividerComponent = styled.div<DividerComponentProps>`
  ${({ $orientation }) => {
    switch ($orientation) {
      case 'vertical':
        return verticalBorderCss;
      case 'horizontal':
      default:
        return horizontalBorderCss;
    }
  }}
  --wui-divider-inset: ${({ $inset }) => `var(--wui-${$inset})`};

  align-self: stretch;
`;

/**
 * A line used to visually separate content; note that dividers have no external margin/spacing on their own.
 */
export const Divider = ({
  orientation = 'horizontal',
  inset = 'space-00',
  ...props
}: DividerProps): JSX.Element => {
  const responsiveOrientation = useResponsiveProp(orientation);
  const responsiveInset = useResponsiveProp(inset);

  return (
    <DividerComponent
      $inset={responsiveInset}
      $orientation={responsiveOrientation}
      aria-orientation={responsiveOrientation}
      role="separator"
      {...props}
    />
  );
};

Divider.displayName = 'Divider';
